/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { highlightColors } from './HighlightColorObject';
import { vehicleColors } from './VehicleColorObject';
type VehicleColorGroup = 'pink' | 'aqua' | 'blue' | 'purple' | 'warm' | 'green' | 'orange' | 'neutral';
type HighlightColorGroup = 'green' | 'blue' | 'yellow' | 'red' | 'neutral';
export type colorPallete = 'vehicle' | 'highlight';

export interface ColorObject {
  id: string;
  name: string;
  group: VehicleColorGroup | HighlightColorGroup | 'custom';
  hex: string;
  rgb: [number, number, number];
  contrastTextHex: string;
  contrastTextRGB: [number, number, number];
  newMappedColorId?: string;
  seriesId?: string;
  vehicleNumber?: string;
}

interface DefaultColorObject extends ColorObject {
  strokeHex: string;
  strokeRGB: [number, number, number];
}

export const defaultLightVehicleColor: DefaultColorObject = {
  id: 'defaultLight',
  name: 'Default Light',
  group: 'neutral',
  hex: '#D9D9D6',
  rgb: [217, 217, 214],
  contrastTextHex: '#000000',
  contrastTextRGB: [0, 0, 0],
  strokeHex: '#3B3B39',
  strokeRGB: [59, 59, 57],
};

export const defaultDarkVehicleColor: DefaultColorObject = {
  id: 'defaultDark',
  name: 'Default Dark',
  group: 'neutral',
  hex: '#595957',
  rgb: [89, 89, 87],
  contrastTextHex: '#FFFFFF',
  contrastTextRGB: [255, 255, 255],
  strokeHex: '#D9D9D6',
  strokeRGB: [217, 217, 214],
};

export const whiteVehicleColor: ColorObject = {
  id: 'white',
  hex: '#ffffff',
  rgb: [255, 255, 255],
  name: 'White',
  group: 'neutral',
  contrastTextHex: '#000000',
  contrastTextRGB: [0, 0, 0],
};

export const genericColors: ReadonlyArray<ColorObject> = [
  {
    id: 'mulberry',
    hex: '#560133',
    rgb: [86, 1, 51],
    name: 'Mulberry',
    group: 'purple',
    contrastTextHex: '#FFFFFF',
    contrastTextRGB: [255, 255, 255],
    newMappedColorId: '23',
  },
  {
    id: 'frenchPlum',
    hex: '#9F0162',
    rgb: [159, 1, 98],
    name: 'French Plum',
    group: 'pink',
    contrastTextHex: '#FFFFFF',
    contrastTextRGB: [255, 255, 255],
    newMappedColorId: '31',
  },
  {
    id: 'persianRose',
    hex: '#EF0096',
    rgb: [239, 0, 150],
    name: 'Persian Rose',
    group: 'pink',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '25',
  },
  {
    id: 'amaranthPink',
    hex: '#FF9DC8',
    rgb: [255, 157, 200],
    name: 'Amaranth Pink',
    group: 'pink',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '33',
  },
  {
    id: 'deepOpal',
    hex: '#005745',
    rgb: [0, 87, 69],
    name: 'Deep Opal',
    group: 'green',
    contrastTextHex: '#FFFFFF',
    contrastTextRGB: [255, 255, 255],
    newMappedColorId: '20',
  },
  {
    id: 'elfGreen',
    hex: '#009175',
    rgb: [0, 145, 117],
    name: 'Elf Green',
    group: 'aqua',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '12',
  },
  {
    id: 'aquamarine',
    hex: '#00CBA7',
    rgb: [0, 203, 167],
    name: 'Aquamarine',
    group: 'aqua',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '21',
  },
  {
    id: 'lightTurquoise',
    hex: '#86FFDE',
    rgb: [134, 255, 222],
    name: 'Light Turquoise',
    group: 'aqua',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '29',
  },
  {
    id: 'purpleHeart',
    hex: '#65019F',
    rgb: [101, 1, 159],
    name: 'Purple Heart',
    group: 'purple',
    contrastTextHex: '#FFFFFF',
    contrastTextRGB: [255, 255, 255],
    newMappedColorId: '7',
  },
  {
    id: 'electricPurple',
    hex: '#A700FC',
    rgb: [167, 0, 252],
    name: 'Electric Purple',
    group: 'purple',
    contrastTextHex: '#FFFFFF',
    contrastTextRGB: [255, 255, 255],
    newMappedColorId: '39',
  },
  {
    id: 'fuchsia',
    hex: '#FF3CFE',
    rgb: [255, 60, 254],
    name: 'Fuchsia',
    group: 'purple',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '25',
  },
  {
    id: 'paleMauve',
    hex: '#FFCCFE',
    rgb: [255, 204, 254],
    name: 'Pale Mauve',
    group: 'purple',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '33',
  },
  {
    id: 'madison',
    hex: '#00306F',
    rgb: [0, 48, 111],
    name: 'Madison',
    group: 'blue',
    contrastTextHex: '#FFFFFF',
    contrastTextRGB: [255, 255, 255],
    newMappedColorId: '6',
  },
  {
    id: 'royalBlue',
    hex: '#005FCC',
    rgb: [0, 95, 204],
    name: 'Royal Blue',
    group: 'blue',
    contrastTextHex: '#FFFFFF',
    contrastTextRGB: [255, 255, 255],
    newMappedColorId: '14',
  },
  {
    id: 'blueDeFrance',
    hex: '#009FFA',
    rgb: [0, 159, 250],
    name: 'Blue de France',
    group: 'blue',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '22',
  },
  {
    id: 'aquaBlue',
    hex: '#00E5F8',
    rgb: [0, 229, 248],
    name: 'Aqua Blue',
    group: 'blue',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '29',
  },
  {
    id: 'hotChile',
    hex: '#7E0018',
    rgb: [126, 0, 24],
    name: 'Hot Chile',
    group: 'warm',
    contrastTextHex: '#FFFFFF',
    contrastTextRGB: [255, 255, 255],
    newMappedColorId: '9',
  },
  {
    id: 'amaranthRed',
    hex: '#CD022D',
    rgb: [205, 2, 45],
    name: 'Amaranth Red',
    group: 'warm',
    contrastTextHex: '#FFFFFF',
    contrastTextRGB: [255, 255, 255],
    newMappedColorId: '17',
  },
  {
    id: 'burningOrange',
    hex: '#FF6E3A',
    rgb: [255, 110, 58],
    name: 'Burning Orange',
    group: 'warm',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '10',
  },
  {
    id: 'gargoyleGas',
    hex: '#FFDC3D',
    rgb: [255, 220, 61],
    name: 'Gargoyle Gas',
    group: 'warm',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '26',
  },
  {
    id: 'britishRacingGreen',
    hex: '#004002',
    rgb: [0, 64, 2],
    name: 'British Racing Green',
    group: 'green',
    contrastTextHex: '#FFFFFF',
    contrastTextRGB: [255, 255, 255],
    newMappedColorId: '4',
  },
  {
    id: 'bilbao',
    hex: '#007702',
    rgb: [0, 119, 2],
    name: 'Bilbao',
    group: 'green',
    contrastTextHex: '#FFFFFF',
    contrastTextRGB: [255, 255, 255],
    newMappedColorId: '12',
  },
  {
    id: 'kellyGreen',
    hex: '#00B408',
    rgb: [0, 180, 8],
    name: 'Kelly Green',
    group: 'green',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '19',
  },
  {
    id: 'radioactiveGreen',
    hex: '#00F407',
    rgb: [0, 244, 7],
    name: 'Radioactive Green',
    group: 'green',
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
    newMappedColorId: '28',
  },
];

export const getColor = (id: string, palette: colorPallete, customColor?: ColorObject): ColorObject => {
  if (customColor && id.includes('custom')) {
    return customColor;
  }
  if (!customColor && id.includes('custom')) {
    return createCustomColorFromId(id);
  }
  return palette === 'vehicle'
    ? vehicleColors.find((color) => color.id === id) || handleStaleVehicleIds(id) || getRandomVehicleColor()
    : highlightColors.find((color) => color.id === id) || getRandomVehicleColor();
};

export const getContrastColor = (refereceColor: string): string => {
  const hex = refereceColor.replace('#', '');

  // Convert the hex color to its RGB components
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate the relative luminance of the background color
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Choose the appropriate text color based on the luminance
  const textColor = luminance > 0.5 ? '#000000' : '#ffffff';

  return textColor;
};

export const hexToRgb = (hex: string): [number, number, number] => {
  let [r, g, b] = [255, 255, 255];
  const value = hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (r, g, b) => '#' + r + r + g + g + b + b);

  if (value) {
    const match = value.substring(1).match(/.{2}/g);
    if (match) [r, g, b] = match.map((x) => parseInt(x, 16));
  }

  return [r, g, b];
};

export const rgbToHex = (rgb: number[]): string => {
  const componentToHex = (intVal: number) => {
    const hex = intVal.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${componentToHex(rgb[0])}${componentToHex(rgb[1])}${componentToHex(rgb[2])}`;
};

export const getRandomVehicleColor = () => {
  return vehicleColors[Math.floor(Math.random() * vehicleColors.length)];
};

export const createCustomColorFromId = (id: string): ColorObject => {
  const array = id.split('-');
  return {
    id: id,
    hex: array[1],
    rgb: hexToRgb(array[1]),
    name: `Custom ${array[1]}`,
    group: 'custom',
    contrastTextHex: getContrastColor(array[1]),
    contrastTextRGB: hexToRgb(getContrastColor(array[1])),
  } as ColorObject;
};

// TODO: This method was made to handle tight coupling of color ids to color names
//       and can be removed after script to remove all stale color id data
export const handleStaleVehicleIds = (colorId: string) => {
  const newColorId = genericColors.find((color) => color.id === colorId)?.newMappedColorId;
  if (newColorId) return vehicleColors.find((color) => color.id === newColorId);
  return null;
};
export const getDefaultVehicleColor = (vehicleNumber: string, seriesId?: string) => {
  if (seriesId) {
    const seriesColor = vehicleColors.find(
      (color) => color.seriesId === seriesId && color.vehicleNumber === vehicleNumber
    );

    if (seriesColor) {
      return seriesColor;
    }
  }

  return getDefaultVehicleColorByVehicleNumberIndex(vehicleNumber);
};

const getDefaultVehicleColorByVehicleNumberIndex = (vehicleNumber: string) => {
  const sorted: ColorObject[] = structuredClone(vehicleColors)
    .concat()
    .sort((a: ColorObject, b: ColorObject) => {
      if (a.id < b.id) {
        return -1;
      }

      if (a.id > b.id) {
        return 1;
      }

      return 0;
    });

  const indexBlack = sorted.findIndex((color) => color.name === 'Black');
  sorted.splice(indexBlack, 1);
  const indexWhite = sorted.findIndex((color) => color.name === 'White');
  sorted.splice(indexWhite, 1);
  const int = parseInt(vehicleNumber);

  return sorted[int % sorted.length];
};
